import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import PublicLayout from "../layouts/PublicLayout";
import Home from "../pages/Home";
import FacilitiesPage from "../pages/FacilitiesPage";
import RentalsPage from "../pages/RentalsPage";
import RulesPage from "../pages/RulesPage";
import DownloadsPage from "../pages/DownloadsPage";
import ContactPage from "../pages/ContactPage";

import AdminLogin from "../pages/admin/AdminLogin";
import AdminDashboard from "../pages/admin/AdminDashboard";
import AdminDiagnostics from "../pages/admin/AdminDiagnostics";
import AdminResidentRequests from "../pages/admin/AdminResidentRequests";
import ApprovedResidents from "../pages/admin/ApprovedResidents";
import RejectedResidents from "../pages/admin/RejectedResidents";
import AdminStats from "../pages/admin/AdminStats";
import AdminNotices from "../pages/admin/AdminNotices";
import TowerAdminRegister from "../pages/admin/TowerAdminRegister";
import AdminTowerRequests from "../pages/admin/AdminTowerRequests";
import AdminAllTowerAdmins from "../pages/admin/AdminAllTowerAdmins";
import AdminMaintenanceView from "../pages/admin/AdminMaintenanceView";
import AdminFmsRequests from "../pages/admin/AdminFmsRequests";
import AdminRules from "../pages/admin/AdminRules";
import AdminVendorDirectory from "../pages/admin/AdminVendorDirectory";
import AdminTheatre from "../pages/admin/AdminTheatre";
import TowerAdminBillingConfig from "../pages/admin/TowerAdminBillingConfig";
import TowerAdminGateShifts from "../pages/admin/TowerAdminGateShifts";
import FMSRegister from "../pages/admin/FMSRegister";
import FMSLogin from "../pages/admin/FMSLogin";
import FMSDashboard from "../pages/admin/FMSDashboard";
import ProtectedFmsRoute from "../components/ProtectedFmsRoute";
import ProtectedAdminRoute from "../components/ProtectedAdminRoute";

import ResidentRegister from "../pages/resident/ResidentRegister";
import ResidentLogin from "../pages/resident/ResidentLogin";
import ResidentDashboard from "../pages/resident/ResidentDashboard";
import ResidentNotices from "../pages/resident/ResidentNotices";
import ResidentMaintenance from "../pages/resident/ResidentMaintenance";
import ResidentProfile from "../pages/resident/ResidentProfile";
import ResidentRentals from "../pages/resident/ResidentRentals";
import ResidentVendors from "../pages/resident/ResidentVendors";
import ResidentTheatre from "../pages/resident/ResidentTheatre";
import ResidentPayments from "../pages/resident/ResidentPayments";
import ResidentVisitorApprovals from "../pages/resident/ResidentVisitorApprovals";
import ProtectedResidentRoute from "../components/ProtectedResidentRoute";
import ResidentLayout from "../layouts/ResidentLayout";
import ResidentPending from "../pages/resident/ResidentPending";

import GateLogin from "../pages/gate/GateLogin";
import GateVisitorEntry from "../pages/gate/GateVisitorEntry";
import GatePreApprovals from "../pages/gate/GatePreApprovals";

import DashboardPage from "../pages/DashboardPage";

function PrivateRoute({ children }) {
  const token = localStorage.getItem("token");
  if (!token) {
    return <Navigate to="/admin/login" replace />;
  }
  return children;
}

export default function AppRoutes() {
  return (
    <BrowserRouter>
      <Routes>

        {/* PUBLIC ROUTES */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<Home />} />
          <Route path="/facilities" element={<FacilitiesPage />} />
          <Route path="/rentals" element={<RentalsPage />} />
          <Route path="/rules" element={<RulesPage />} />
          <Route path="/downloads" element={<DownloadsPage />} />
          <Route path="/contact" element={<ContactPage />} />

          {/* RESIDENT AUTH */}
          <Route path="/resident/login" element={<ResidentLogin />} />
          <Route path="/resident/register" element={<ResidentRegister />} />
          <Route path="/resident/pending" element={<ResidentPending />} />
        </Route>

        {/* ADMIN AUTH — MUST BE OUTSIDE PUBLIC LAYOUT */}
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin/register-tower" element={<TowerAdminRegister />} />

        {/* GATE STAFF AUTH — MUST BE OUTSIDE PUBLIC LAYOUT */}
        <Route path="/gate/login" element={<GateLogin />} />
        <Route path="/gate/visitor-entry" element={<GateVisitorEntry />} />
        <Route path="/gate/pre-approvals" element={<GatePreApprovals />} />

        {/* RESIDENT DASHBOARD — WITH LAYOUT AND SIDEBAR */}
        <Route
          path="/resident"
          element={
            <ProtectedResidentRoute>
              <ResidentLayout />
            </ProtectedResidentRoute>
          }
        >
          <Route path="dashboard" element={<ResidentDashboard />} />
          <Route path="payments" element={<ResidentPayments />} />
          <Route path="notices" element={<ResidentNotices />} />
          <Route path="maintenance" element={<ResidentMaintenance />} />
          <Route path="profile" element={<ResidentProfile />} />
          <Route path="rentals" element={<ResidentRentals />} />
          <Route path="vendors" element={<ResidentVendors />} />
          <Route path="theatre" element={<ResidentTheatre />} />
          <Route path="visitor-approvals" element={<ResidentVisitorApprovals />} />
        </Route>

        {/* ADMIN DASHBOARD */}
        <Route
          path="/admin/dashboard"
          element={
            <PrivateRoute>
              <AdminDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/admin/diagnostics"
          element={
            <ProtectedAdminRoute>
              <AdminDiagnostics />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/residents"
          element={
            <ProtectedAdminRoute>
              <AdminResidentRequests />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/approved"
          element={
            <ProtectedAdminRoute>
              <ApprovedResidents />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/rejected"
          element={
            <ProtectedAdminRoute>
              <RejectedResidents />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/notices"
          element={
            <ProtectedAdminRoute>
              <AdminNotices />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/stats"
          element={
            <ProtectedAdminRoute>
              <AdminStats />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/tower-requests"
          element={
            <ProtectedAdminRoute>
              <AdminTowerRequests />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/tower-admins-all"
          element={
            <ProtectedAdminRoute>
              <AdminAllTowerAdmins />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/maintenance"
          element={
            <ProtectedAdminRoute>
              <AdminMaintenanceView />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/fms-requests"
          element={
            <ProtectedAdminRoute>
              <AdminFmsRequests />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/rules"
          element={
            <ProtectedAdminRoute>
              <AdminRules />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/vendors"
          element={
            <ProtectedAdminRoute>
              <AdminVendorDirectory />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/theatre"
          element={
            <ProtectedAdminRoute>
              <AdminTheatre />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/billing-config"
          element={
            <ProtectedAdminRoute>
              <TowerAdminBillingConfig />
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/admin/gate-shifts"
          element={
            <ProtectedAdminRoute>
              <TowerAdminGateShifts />
            </ProtectedAdminRoute>
          }
        />
        <Route path="/fms/register" element={<FMSRegister />} />
        <Route path="/fms/login" element={<FMSLogin />} />
        <Route
          path="/fms/dashboard"
          element={
            <ProtectedFmsRoute>
              <FMSDashboard />
            </ProtectedFmsRoute>
          }
        />

        {/* OLD DASHBOARD */}
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <DashboardPage />
            </PrivateRoute>
          }
        />

        {/* FALLBACK */}
        <Route path="*" element={<Navigate to="/" replace />} />

      </Routes>
    </BrowserRouter>
  );
}